import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus, MonitorDown, Check } from "lucide-react";

// --- Capture globale de l'événement d'installation (ne se déclenche qu'une fois) ---
let _deferredPrompt = null;
const _listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e;
    _listeners.forEach((fn) => fn());
  });
  window.addEventListener("appinstalled", () => {
    _deferredPrompt = null;
    _listeners.forEach((fn) => fn());
  });
}

export const isStandalone = () =>
  (typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true));

export const isIOS = () =>
  typeof window !== "undefined" && /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

// Hook partagé : disponibilité de l'installation + déclenchement.
export function useInstallPrompt() {
  const [available, setAvailable] = useState(!!_deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const fn = () => { setAvailable(!!_deferredPrompt); setInstalled(isStandalone()); };
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);
  const promptInstall = async () => {
    if (!_deferredPrompt) return false;
    _deferredPrompt.prompt();
    const res = await _deferredPrompt.userChoice;
    _deferredPrompt = null;
    setAvailable(false);
    return res && res.outcome === "accepted";
  };
  return { available, installed, ios: isIOS(), promptInstall };
}

function IOSHelp({ onClose }) {
  return (
    <div className="bg-[#0b0b0e] border border-white/15 rounded-2xl p-4 shadow-2xl text-white" data-testid="pwa-ios-help">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm font-medium">Ajouter à l'écran d'accueil</p>
        <button onClick={onClose} data-testid="pwa-close" className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <ol className="text-xs text-zinc-300 space-y-2 leading-relaxed">
        <li className="flex items-center gap-2">1. Touchez <Share className="w-4 h-4 inline text-[#C19A6B]" /> (Partager)</li>
        <li className="flex items-center gap-2">2. Choisissez <Plus className="w-4 h-4 inline text-[#C19A6B]" /> « Sur l'écran d'accueil »</li>
        <li>3. Validez « Ajouter »</li>
      </ol>
    </div>
  );
}

// Bouton flottant public (bottom-left). Masqué dans le back-office (bannière dédiée).
export default function InstallApp() {
  const { available, installed, ios, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);
  const [showIos, setShowIos] = useState(false);
  useEffect(() => {
    if (ios && !installed) { const t = setTimeout(() => setShowIos(true), 2500); return () => clearTimeout(t); }
  }, [ios, installed]);

  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const inBackOffice = path.startsWith("/mon-compte") || path.startsWith("/admin");
  if (installed || dismissed || inBackOffice) return null;
  if (!available && !(ios && showIos)) return null;

  return (
    <div className="fixed bottom-5 left-5 z-[70] max-w-[280px]" data-testid="pwa-install">
      {iosHelp ? (
        <IOSHelp onClose={() => { setIosHelp(false); setDismissed(true); }} />
      ) : (
        <div className="flex items-center gap-2 bg-[#0b0b0e] border border-white/15 rounded-full pl-4 pr-2 py-2 shadow-2xl" data-testid="pwa-install-bar">
          <button onClick={() => (available ? promptInstall() : setIosHelp(true))} data-testid="pwa-install-btn" className="flex items-center gap-2 text-sm text-white font-medium">
            <Download className="w-4 h-4 text-[#C19A6B]" /> Installer l'app
          </button>
          <button onClick={() => setDismissed(true)} data-testid="pwa-dismiss" className="text-zinc-500 hover:text-white ml-1"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

// Bannière motivante à l'arrivée dans le back-office (installation sur l'ordinateur).
export function InstallBackOfficeBanner() {
  const { available, installed, ios, promptInstall } = useInstallPrompt();
  const [hidden, setHidden] = useState(false);
  const [done, setDone] = useState(false);
  const [help, setHelp] = useState(null); // null | 'ios' | 'desktop'
  useEffect(() => { if (localStorage.getItem("bo-install-hidden") === "1") setHidden(true); }, []);
  const hide = () => { setHidden(true); localStorage.setItem("bo-install-hidden", "1"); };

  if (installed || hidden) return null;

  const onInstall = async () => {
    if (available) { const ok = await promptInstall(); if (ok) setDone(true); }
    else if (ios) setHelp("ios");
    else setHelp("desktop");
  };

  return (
    <div className="mb-5 rounded-2xl border border-[#C19A6B]/30 bg-gradient-to-r from-[#C19A6B]/10 to-transparent p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="bo-install-banner">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span className="shrink-0 w-11 h-11 rounded-xl bg-[#C19A6B]/15 border border-[#C19A6B]/30 flex items-center justify-center"><MonitorDown className="w-5 h-5 text-[#C19A6B]" /></span>
        <div className="min-w-0">
          <p className="text-white font-medium text-sm">Installez le Back-Office sur votre {ios ? "appareil" : "ordinateur"}</p>
          <p className="text-zinc-400 text-xs mt-0.5">Un accès en un clic depuis votre bureau, comme une vraie application — plus rapide, sans passer par le navigateur.</p>
          {help === "desktop" && <p className="text-[#C19A6B] text-xs mt-2" data-testid="bo-install-desktop-help">Dans Chrome ou Edge : cliquez sur l'icône <span className="font-semibold">⊕ « Installer »</span> à droite de la barre d'adresse, ou menu <span className="font-semibold">⋮ → Installer l'application</span>.</p>}
          {help === "ios" && <p className="text-[#C19A6B] text-xs mt-2" data-testid="bo-install-ios-help">Sur iPhone/iPad : touchez <span className="font-semibold">Partager</span> puis <span className="font-semibold">« Sur l'écran d'accueil »</span>.</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {done ? (
          <span className="inline-flex items-center gap-1.5 text-emerald-400 text-sm" data-testid="bo-install-done"><Check className="w-4 h-4" /> Installé</span>
        ) : (
          <button onClick={onInstall} data-testid="bo-install-btn" className="inline-flex items-center gap-2 rounded-full bg-[#C19A6B] text-white text-xs font-semibold uppercase tracking-wider px-4 py-2.5 hover:brightness-110 transition-all">
            <Download className="w-4 h-4" /> Installer
          </button>
        )}
        <button onClick={hide} data-testid="bo-install-dismiss" className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
